/**
 * Global 2FA modal — mounted once at app shell level.
 *
 * Auto-pops whenever the head of the twoFAQueue changes (a new job needs a
 * code). Also listens for `scrapeJobs.openTwoFA` window events so any
 * surface that has an "awaiting 2FA" row can jump a specific job to the
 * front and re-open the modal even after dismissal.
 *
 * Submit POSTs to /clinics/api/scrape-jobs/{id}/twofa/. The signal in
 * clinics/signals.py mirrors PendingUserInput2 -> ScrapeJob, so the job
 * transitions back to RUNNING automatically.
 *
 * react-redux 6.x — uses connect() (no hooks).
 */

import React from "react";
import { connect } from "react-redux";
import { Button, Input, Modal, Tag, message } from "antd";

import { apiService } from "../../_services/api.service";
import { debugLog } from "../../utils/debugLog";
import {
  selectAwaitingTwoFAJobs,
  selectTwoFAQueueHead,
  SCRAPE_JOB_UPSERT,
} from "../../reducers/scrapeJobs";

const PURPLE = "#6B43B5";
const TWOFA_TIMEOUT_SECONDS = 300; // matches action_scraper.py:29-48

function jobLabel(job) {
  const op = (job.operation_type || "").replace(/_/g, " ");
  return op.charAt(0).toUpperCase() + op.slice(1);
}

function secondsLeft(job, nowMs) {
  if (!job || !job.twofa_required_at) return null;
  const requiredAt = new Date(job.twofa_required_at).getTime();
  const elapsed = Math.max(0, Math.floor((nowMs - requiredAt) / 1000));
  return Math.max(0, TWOFA_TIMEOUT_SECONDS - elapsed);
}

function formatCountdown(sec) {
  if (sec == null) return "";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m + ":" + (s < 10 ? "0" + s : s);
}

class GlobalScrapeJob2FAModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      code: "",
      submitting: false,
      pinnedJobId: null,
      now: Date.now(),
    };
    this.dismissed = {};
    this.tickTimer = null;
  }

  componentDidMount() {
    window.addEventListener("scrapeJobs.openTwoFA", this.handleOpenTwoFAEvent);
    this.maybeOpenForActiveJob();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.queueHead !== this.props.queueHead || prevState.pinnedJobId !== this.state.pinnedJobId) {
      this.maybeOpenForActiveJob();
    }
    if (this.state.open && !this.tickTimer) {
      this.tickTimer = setInterval(() => this.setState({ now: Date.now() }), 1000);
    } else if (!this.state.open && this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scrapeJobs.openTwoFA", this.handleOpenTwoFAEvent);
    if (this.tickTimer) clearInterval(this.tickTimer);
  }

  getActiveJob() {
    const { queue, queueHead } = this.props;
    const { pinnedJobId } = this.state;
    if (pinnedJobId) {
      const pinned = queue.find((j) => j.job_id === pinnedJobId);
      if (pinned) return pinned;
    }
    return queueHead;
  }

  maybeOpenForActiveJob() {
    const job = this.getActiveJob();
    if (job && !this.dismissed[job.job_id]) {
      if (!this.state.open) {
        this.setState({ open: true, code: "" });
        debugLog.info("twofa", "twofa_modal_shown", {
          job_id: job.job_id,
          carrier_code: job.carrier_code,
        });
      }
    } else if (!job) {
      if (this.state.open) this.setState({ open: false });
    }
  }

  handleOpenTwoFAEvent = (ev) => {
    const id = ev && ev.detail && ev.detail.jobId;
    if (!id) return;
    delete this.dismissed[id];
    this.setState({ pinnedJobId: id, open: true, code: "" });
  };

  handleSubmit = async () => {
    const job = this.getActiveJob();
    if (!job) return;
    const trimmed = (this.state.code || "").trim();
    if (!trimmed) {
      message.warning("Enter the verification code from the carrier.");
      return;
    }
    debugLog.info("twofa", "twofa_submit", {
      job_id: job.job_id,
      code_length: trimmed.length,
    });
    this.setState({ submitting: true });
    try {
      const data = await apiService.post(
        "/clinics/api/scrape-jobs/" + job.job_id + "/twofa/",
        { code: trimmed }
      );
      if (data && data.job_id) this.props.upsertJob(data);
      message.success("Code submitted.");
      this.setState({ pinnedJobId: null, open: false, code: "", submitting: false });
    } catch (e) {
      message.error("Could not submit the code.");
      this.setState({ submitting: false });
    }
  };

  handleHandleLater = () => {
    const job = this.getActiveJob();
    if (job) {
      this.dismissed[job.job_id] = true;
      debugLog.info("twofa", "twofa_dismiss", { job_id: job.job_id, reason: "handle_later" });
    }
    this.setState({ pinnedJobId: null, open: false, code: "" });
  };

  render() {
    const job = this.getActiveJob();
    if (!job) return null;
    const remaining = secondsLeft(job, this.state.now);
    const expired = remaining != null && remaining <= 0;
    const queueLen = this.props.queue.length;

    return (
      <Modal
        open={this.state.open}
        title="Enter verification code"
        maskClosable={false}
        onCancel={this.handleHandleLater}
        footer={[
          <Button key="later" onClick={this.handleHandleLater}>Handle later</Button>,
          <Button
            key="submit"
            type="primary"
            loading={this.state.submitting}
            disabled={expired}
            onClick={this.handleSubmit}
            style={{ background: PURPLE, borderColor: PURPLE }}
          >
            Submit
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>
            {job.patient_name || "Patient #" + job.patient_id}
          </div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            <Tag color="gold">2FA needed</Tag>
            <Tag>{job.carrier_code}</Tag>
            <Tag>{jobLabel(job)}</Tag>
          </div>
        </div>
        <p style={{ marginBottom: 8 }}>
          The carrier portal sent a verification code. Enter it below to keep
          the fetch going.
        </p>
        <Input
          autoFocus
          value={this.state.code}
          placeholder="Code"
          onChange={(e) => this.setState({ code: e.target.value })}
          onPressEnter={this.handleSubmit}
          disabled={expired || this.state.submitting}
        />
        {remaining != null ? (
          <div style={{ marginTop: 10, fontSize: 12, color: expired ? "#ff4d4f" : "#666" }}>
            {expired ? "Code expired — please retry the fetch." : "Time left: " + formatCountdown(remaining)}
          </div>
        ) : null}
        {queueLen > 1 ? (
          <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
            +{queueLen - 1} more 2FA request{queueLen - 1 === 1 ? "" : "s"} waiting
          </div>
        ) : null}
      </Modal>
    );
  }
}

const mapStateToProps = (state) => ({
  queue: selectAwaitingTwoFAJobs(state),
  queueHead: selectTwoFAQueueHead(state),
});

const mapDispatchToProps = (dispatch) => ({
  upsertJob: (job) => dispatch({ type: SCRAPE_JOB_UPSERT, job }),
});

export default connect(mapStateToProps, mapDispatchToProps)(GlobalScrapeJob2FAModal);
